import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'

import { Box, Breakpoint, Grid, GridSpan, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { EyeVisible } from '@factorialco/f0-react/icons/app'
import {
  CheckSubscriptionItemsInstalledDocument,
  NextInvoiceDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useQuery } from 'modules/core/hooks/useQuery'

import { formatPrice } from 'modules/billing/lib/f25Prices'
import {
  MarketplacePricingCard,
  PricingPlanProps,
} from 'modules/marketplace_modules/components/Revamp/types'
import { useMarketplaceUpsellRequest } from 'modules/marketplace_modules/hooks/useMarketplaceUpsellRequest'
import { useHasRequestedToday } from 'modules/marketplace_modules/hooks/useHasRequestedToday'
import { isValidModule } from 'modules/marketplace_modules/lib/typesValidation'
import CheckoutModal from 'modules/upsell/components/SelfUpsell/CheckoutModal'
import type PlanVariant from 'modules/upsell/lib/selfUpsell/plans/PlanVariant'
import {
  BUSINESS_VARIANT,
  ENTERPRISE_VARIANT,
  YEAR_PERIOD,
  getOneInstallationInfo,
  getOnePlanInstallationLabel,
  isOnePlan,
} from 'modules/upsell/lib/selfUpsell/plans/utils'
import { PeriodUnit, UpsellDiscount } from 'modules/upsell/lib/selfUpsell/types'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'
import { useLocalStorageWithExpiration } from 'modules/upselling_kit/utils/localStorage'

import { Item } from './_Item'
import { ComparisonModal } from './_Modal'

const getPricingInfo = (
  specificVariant: PlanVariant | undefined,
  period: PeriodUnit,
  currency: string,
  seats: number,
  discount: UpsellDiscount | undefined,
  hasMoreThanTwoPlans = false
) => {
  if (!specificVariant) return { price: '', description: '' }

  const isRecruitmentModule = specificVariant.id.toLowerCase().includes('recruitment')
  const hasDiscount = discount && discount.percentage > 0
  const perSeatPrice = specificVariant.pricing.getSeatsPrice(period)
  const feePrice =
    period === YEAR_PERIOD
      ? specificVariant.pricing.getAnnualFeePrice(period)
      : specificVariant.pricing.getFeePrice(period)
  const stairstepPrice = specificVariant.pricing.getStairstepPrice(period)
  const tieredPrice = specificVariant.pricing.getTieredPrice(period, seats)

  const includedSeatsPerModule = tieredPrice?.tiers?.[0]?.endingUnit
  const pricePerUser = tieredPrice?.tiers?.[1]?.price || 0

  const calculateDiscountedPrice = (originalPrice: number) => {
    if (!hasDiscount) return originalPrice
    return originalPrice * (1 - discount.percentage / 100)
  }

  if (perSeatPrice) {
    if (hasDiscount) {
      return {
        originalPrice: formatPrice(perSeatPrice, currency),
        price: formatPrice(calculateDiscountedPrice(perSeatPrice), currency),
        description: i18n.t('self_upsell.first_step.seats_month'),
        hasDiscount: true,
      }
    } else {
      return {
        price: formatPrice(perSeatPrice, currency),
        description: i18n.t('self_upsell.first_step.seats_month'),
        hasDiscount: false,
      }
    }
  }

  if (feePrice) {
    let description = i18n.t('pricing_plans.fixed_fee')
    let seatDescription = ''
    let seatOriginalPrice = ''
    if (hasMoreThanTwoPlans) {
      description = `${i18n.t('pricing_plans.from')} ${formatPrice(
        calculateDiscountedPrice(feePrice),
        currency
      )} ${isRecruitmentModule ? i18n.t('pricing_plans.fixed_fee') : ''}`
    } else {
      if (includedSeatsPerModule) {
        description += `, ${i18n.t('pricing_plans.employees_inludede', {
          employees: includedSeatsPerModule,
        })}`
        if (tieredPrice) {
          seatOriginalPrice = formatPrice(pricePerUser / 100, currency)
          seatDescription += i18n.t('pricing_plans.per_new_employee', {
            amount: formatPrice(calculateDiscountedPrice(pricePerUser / 100), currency),
          })
        }
      }
    }

    if (hasDiscount) {
      return {
        originalPrice: formatPrice(feePrice, currency),
        price: formatPrice(calculateDiscountedPrice(feePrice), currency),
        description,
        hasDiscount: true,
        seatDescription,
        seatOriginalPrice,
      }
    } else {
      return {
        price: formatPrice(feePrice, currency),
        description,
        hasDiscount: false,
        seatDescription,
      }
    }
  }

  if (stairstepPrice) {
    let description = i18n.t('pricing_plans.fixed_fee')
    if (hasMoreThanTwoPlans) {
      description = `${i18n.t('pricing_plans.from')} ${formatPrice(
        calculateDiscountedPrice(stairstepPrice),
        currency
      )} ${isRecruitmentModule ? i18n.t('pricing_plans.fixed_fee') : ''}`
    }

    if (hasDiscount) {
      return {
        originalPrice: formatPrice(stairstepPrice, currency),
        price: formatPrice(calculateDiscountedPrice(stairstepPrice), currency),
        description,
        hasDiscount: true,
      }
    } else {
      return {
        price: formatPrice(stairstepPrice, currency),
        description,
        hasDiscount: false,
      }
    }
  }

  return { price: '', description: '', hasDiscount: false }
}

const PricingPlan: React.FC<PricingPlanProps> = ({
  module,
  isAuthorized,
  chargebeeModuleName,
  addonReference,
  moduleData,
  canSeeSelfUpsell,
  canSeeNormalUpsell,
}) => {
  const [isComparisonModalOpen, setIsComparisonModalOpen] = useState(false)
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false)
  const { pathname } = useLocation()
  const [storedUpsellOrigin] = useLocalStorageWithExpiration<{
    request_source: string
    request_source_category?: string
    request_source_component?: string
  }>('upsell_origin', 5)
  const upsellOrigin: string | undefined = storedUpsellOrigin?.request_source
  const upsellOriginCategory = storedUpsellOrigin?.request_source_category
  const upsellOriginComponent = storedUpsellOrigin?.request_source_component
  const {
    allAddons,
    currency,
    period,
    seats: _seats,
    computePerSeatPrice: _computePerSeatPrice,
    computeTotalPrice: _computeTotalPrice,
    discount,
  } = useSelfUpsellContext()

  const validChargebeeModuleName = isValidModule(chargebeeModuleName)
    ? chargebeeModuleName
    : undefined

  const { executeUpsellRequest, UpsellDialog } = useMarketplaceUpsellRequest({
    moduleSlug: module,
    moduleRequested: validChargebeeModuleName,
    requestSource: upsellOrigin || `Marketplace`,
    pageSource: pathname,
    requestSourceCategory: upsellOriginCategory,
    requestSourceComponent: upsellOriginComponent,
    hubspotModuleName: moduleData?.hubspotModuleName || validChargebeeModuleName,
  })
  const { hasRequestedToday, loading: hasRequestedTodayLoading } =
    useHasRequestedToday(validChargebeeModuleName)

  const handleRequestUpsell = () => {
    executeUpsellRequest()
  }

  const handleOpenComparisonModal = () => {
    track(trackingEvents.marketplaceModules.opensCompareFeaturesModal, {
      source: `marketplace_pricing_compare_features`,
      module: module,
      origin: pathname,
    })
    setIsComparisonModalOpen(true)
  }

  const handleViewAllFeatures = () => {
    track(trackingEvents.marketplaceModules.opensCompareFeaturesModal, {
      source: `marketplace_pricing_view_all_features`,
      module: module,
      origin: pathname,
    })
    setIsComparisonModalOpen(true)
  }

  const handleCloseComparisonModal = () => {
    setIsComparisonModalOpen(false)
  }

  const handleOpenCheckoutModal = (selectedPlan?: MarketplacePricingCard) => {
    track(trackingEvents.marketplaceModules.openCheckoutModal, {
      source: 'marketplace_pricing_plan_checkout_modal',
      module: module,
      origin: pathname,
    })
    if (selectedPlan?.plan && selectedPlan?.variant) {
      allAddons.f25Addons.forEach((plan) => plan.unselect())
      selectedPlan.plan.select()
      selectedPlan.plan.setActiveVariant(selectedPlan.variant)
    }
    setIsCheckoutModalOpen(true)
  }

  const handleCloseCheckoutModal = () => {
    setIsCheckoutModalOpen(false)
  }

  const { company } = useCurrentSession()

  const isOneModule = allAddons.f25Addons.some((plan) => isOnePlan(plan.internalTitle))

  const { data: subscriptionItemsData } = useQuery(CheckSubscriptionItemsInstalledDocument, {
    variables: {},
    skip: !isOneModule,
  })

  const { data: nextInvoiceData } = useQuery(NextInvoiceDocument, {
    variables: { companyId: company.id },
    skip: !isOneModule,
  })

  const oneInstallationInfo = getOneInstallationInfo({
    onePlans: allAddons.f25Addons,
    installedAddonInternalId: moduleData?.installedAddonInternalId ?? null,
    period,
    subscriptionItemsData,
    nextInvoiceData,
  })

  const dynamicPlans = allAddons.f25Addons.flatMap((plan) => {
    const variants = plan.variantTypes.filter(
      (variant) => variant === BUSINESS_VARIANT || variant === ENTERPRISE_VARIANT
    )

    return variants.map((variant) => {
      const specificVariant = plan.getSpecificVariant(variant)
      const features = plan.getFeatures(variant)

      return {
        variant,
        specificVariant,
        features,
        plan,
      }
    })
  })

  const hasMoreThanTwoPlans = dynamicPlans.length > 2
  const limitedPlans = isOneModule
    ? dynamicPlans
    : hasMoreThanTwoPlans
      ? dynamicPlans.slice(0, 2)
      : dynamicPlans

  const gridColSpan: { [key in Breakpoint]?: GridSpan } = {
    xxs: 12,
    s: isOneModule ? 4 : 6,
  }


  const validPlans = limitedPlans.map(({ variant, specificVariant, features, plan }) => {
    const pricingInfo = getPricingInfo(
      specificVariant,
      period,
      currency,
      _seats,
      discount,
      hasMoreThanTwoPlans
    )

    const limitedFeatures = features.slice(0, variant === 'Enterprise' ? 2 : 3)
    const featureTitles = limitedFeatures.map((feature) => feature.title)

    const minLines = 3
    while (featureTitles.length < minLines) {
      featureTitles.push(' ')
    }

    const displayPerks = featureTitles.join('\n')

    return {
      __typename: 'MarketplacePricingCard' as const,
      name: isOneModule ? plan.title : variant,
      price: hasMoreThanTwoPlans
        ? pricingInfo.description
        : pricingInfo.price + ' ' + pricingInfo.description,
      originalPrice: pricingInfo.originalPrice,
      hasDiscount: pricingInfo.hasDiscount,
      perks: displayPerks,
      perksExtended: features.map((feature) => feature.title).join('\n'),
      icon: variant === BUSINESS_VARIANT ? '💼' : variant === ENTERPRISE_VARIANT ? '🚀' : '❓',
      plan,
      variant,
      seatDescription: pricingInfo.seatDescription,
      seatOriginalPrice: pricingInfo.seatOriginalPrice,
      ...(isOneModule ? (() => {
        const label = getOnePlanInstallationLabel(plan, oneInstallationInfo)
        return {
          isInstalledOnePlan: label.isInstalled,
          isMaxInstalledOnePlan: label.isMaxInstalled,
          isDowngradeOnePlan: label.isDowngrade,
          installedOnePlanUntilDate: label.untilDate,
        }
      })() : {}),
    }
  })

  const allPlans = validPlans

  if (allPlans.length === 0) {
    return null
  }

  return (
    <Box paddingY="s24">
      <Box paddingBottom="s24" flexDirection="row" justifyContent="spaceBetween">
        <Text size="300" weight="semibold" color="black">
          {i18n.t('marketplace.revamp.pricing')}
        </Text>
        <F0Button
          icon={EyeVisible}
          label={i18n.t('marketplace.revamp.compare_features')}
          variant="outline"
          onClick={handleOpenComparisonModal}
        />
      </Box>
      <Grid variant="fixed" style={{ alignItems: 'stretch' }}>
        {allPlans.map((plan: MarketplacePricingCard) => (
          <Box key={plan.name} gridColSpan={gridColSpan} style={{ height: '100%' }}>
            <Item
              addonReference={addonReference}
              isAuthorized={isAuthorized}
              plan={plan}
              onViewAllFeatures={handleViewAllFeatures}
              onGetItNow={() => handleOpenCheckoutModal(plan)}
              onRequestUpsell={() => handleRequestUpsell()}
              hasRequestedToday={hasRequestedToday}
              hasRequestedTodayLoading={hasRequestedTodayLoading}
              moduleData={moduleData || undefined}
              canSeeSelfUpsell={canSeeSelfUpsell}
              canSeeNormalUpsell={canSeeNormalUpsell}
            />
          </Box>
        ))}
      </Grid>

      <ComparisonModal
        isOpen={isComparisonModalOpen}
        onClose={handleCloseComparisonModal}
        plans={allPlans.slice(0, 2)}
      />

      {isValidModule(chargebeeModuleName) && (
        <CheckoutModal
          isOpen={isCheckoutModalOpen}
          onClose={handleCloseCheckoutModal}
          moduleName={chargebeeModuleName}
          moduleSlug={moduleData?.slug || ''}
          hubspotModuleName={moduleData?.hubspotModuleName}
          from="marketplace_pricing_card"
        />
      )}

      <UpsellDialog />
    </Box>
  )
}

export default PricingPlan
